import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { data, portal, type Edition } from "@aawaz/api-client";
import { t } from "@aawaz/i18n";
import {
  Badge, Button, Card, CopyButton, Empty, ErrorBanner, Field, Icon, Input, Modal, Notice, PageHeader, Select, Spinner, Table, fmtDate, initials, maskPhone,
  type IconName,
} from "@aawaz/ui";
import { useClients } from "../components/ItemPanel";
import { Automation } from "./Automation";
import { TrustPage } from "./Trust";
import { useSession, type Perm } from "../session";

type Section = { value: string; label: string; icon: IconName; hint: string; perm?: Perm };
const SECTIONS: Section[] = [
  { value: "firm", label: "Firm", icon: "building", hint: "Profile, plan and document rules" },
  { value: "team", label: "Team", icon: "users", hint: "People, roles and client access", perm: "manage_members" },
  { value: "automation", label: "Automation", icon: "sparkles", hint: "What AawazAI does on its own", perm: "tenant_settings" },
  { value: "security", label: "Security & data", icon: "shield", hint: "Where data lives, who opened what, exports" },
  { value: "developers", label: "Developers", icon: "code", hint: "API keys and webhooks", perm: "manage_api" },
  { value: "account", label: "My account", icon: "user", hint: "Your signed-in devices" },
];

/** Settings, grouped the way a firm thinks about it: who we are, who works here, what runs on its own,
 *  how our data is kept, and the developer surface. Each section has its own address (/settings/<section>). */
export function SettingsPage() {
  const s = useSession();
  const loc = useLocation();
  const navigate = useNavigate();
  const sections = SECTIONS.filter((x) => !x.perm || s.can(x.perm));
  const sub = loc.pathname.split("/")[2] ?? "";
  const cur = sections.find((x) => x.value === sub) ?? sections[0]!;
  const denied = SECTIONS.find((x) => x.value === sub && !sections.includes(x));  // a link to a page this role cannot open
  return (
    <>
      <PageHeader title="Settings" description={cur.hint} />
      <div className="ui-settings">
        <nav className="ui-subnav" aria-label="Settings">
          {sections.map((x) => (
            <button key={x.value} className={x.value === cur.value ? "ui-subnav-item ui-on" : "ui-subnav-item"}
                    aria-current={x.value === cur.value ? "page" : undefined} onClick={() => navigate(`/settings/${x.value}`)}>
              <Icon name={x.icon} size={17} /><span>{x.label}</span>
            </button>
          ))}
        </nav>
        <div className="ui-stack">
          {denied ? <Notice>{denied.label} settings can be opened by {denied.perm === "tenant_settings" ? "the firm's owner" : "an owner or admin"} only. Showing {cur.label} instead.</Notice> : null}
          {cur.value === "firm" ? <><Firm /><Policy /></> : null}
          {cur.value === "team" ? <Members /> : null}
          {cur.value === "automation" ? <Automation /> : null}
          {cur.value === "security" ? <><TrustPage embedded />{s.can("audit") ? <Audit /> : null}<Support />{s.can("tenant_settings") ? <DataTab /> : null}</> : null}
          {cur.value === "developers" ? <><ApiKeys /><Webhooks /></> : null}
          {cur.value === "account" ? <Devices /> : null}
        </div>
      </div>
    </>
  );
}

const EDITION_INFO: Record<Edition, { title: string; text: string }> = {
  ca: { title: "CA practice", text: "Clients, GST desk with GSTR-2B matching, document reminders, staff forwarding and readiness for every return." },
  accounting: { title: "Accounting", text: "Your own bills read and filed, synced with Zoho Books or Tally, and verified e-invoices posted for you." },
};

function Firm() {
  const s = useSession();
  const firm = s.me?.firm;
  const firmName = firm?.name ?? s.me?.memberships.find((m) => m.tenant_id === s.me?.tenant_id)?.name ?? "Your firm";
  const eds = firm?.editions ?? ["ca"];
  return (
    <Card title="Your firm" subtitle="Your plan decides which screens you see. To add or change one, write to support@aawazai.com.">
      <div className="ui-firm-profile">
        <span className="ui-firm-mark ui-firm-mark-lg" aria-hidden="true">{initials(firmName)}</span>
        <div>
          <div className="ui-figure" style={{ fontSize: 20 }}>{firmName}</div>
          <span className="ui-hint">{firm?.kind === "business" ? "Business" : firm?.kind === "platform" ? "Platform" : "Chartered accountants"}</span>
        </div>
      </div>
      <div className="ui-tiles" style={{ marginTop: 16 }}>
        {(Object.keys(EDITION_INFO) as Edition[]).map((e) => (
          <div key={e} className={eds.includes(e) ? "ui-plan ui-plan-on" : "ui-plan"}>
            <div className="ui-row" style={{ justifyContent: "space-between" }}>
              <strong>{EDITION_INFO[e].title}</strong>
              {eds.includes(e) ? <Badge value="included" tone="green" /> : <Badge value="not included" tone="grey" />}
            </div>
            <p className="ui-hint" style={{ margin: "6px 0 0" }}>{EDITION_INFO[e].text}</p>
          </div>
        ))}
      </div>
    </Card>
  );
}

const roleLabel = (r: string) => r.charAt(0).toUpperCase() + r.slice(1);

interface Member { id: string; role: string; client_access: string; status: string; name: string | null; phone: string; client_org_ids?: string[] }

function Members() {
  const s = useSession();
  const qc = useQueryClient();
  const clients = useClients();
  const q = useQuery({ queryKey: ["members"], queryFn: () => data<{ data: Member[] }>(portal.GET("/portal/v1/members")) });
  const [inv, setInv] = useState({ phone: "+91", name: "", role: "staff", client_access: "all" });
  const [assign, setAssign] = useState<Member | null>(null);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const refresh = () => qc.invalidateQueries({ queryKey: ["members"] });
  const [invited, setInvited] = useState<{ phone: string; text: string } | null>(null);
  const invite = useMutation({ mutationFn: () => data(portal.POST("/portal/v1/members", { body: { ...inv, role: inv.role as "staff", client_access: inv.client_access } })),
    onSuccess: () => {
      setInvited({ phone: inv.phone, text: `${inv.name ? `Hi ${inv.name.split(" ")[0]}, you` : "You"} have been added to ${s.me?.firm?.name ?? "our firm"} on AawazAI Connect. ` +
        `Sign in at ${window.location.origin} with this WhatsApp number; a login code is sent to you on WhatsApp.` });
      setInv({ phone: "+91", name: "", role: "staff", client_access: "all" }); void refresh();
    } });
  const patch = useMutation({ mutationFn: ({ id, body }: { id: string; body: { role?: string; client_access?: string } }) =>
    data(portal.PATCH("/portal/v1/members/{mid}", { params: { path: { mid: id } }, body: body as never })), onSuccess: refresh });
  const remove = useMutation({ mutationFn: (id: string) => portal.DELETE("/portal/v1/members/{mid}", { params: { path: { mid: id } } }), onSuccess: refresh });
  const revoke = useMutation({ mutationFn: (id: string) => data(portal.POST("/portal/v1/members/{mid}/sessions:revoke", { params: { path: { mid: id } } })) });
  const saveAssign = useMutation({ mutationFn: () => data(portal.PUT("/portal/v1/members/{mid}/clients",
    { params: { path: { mid: assign!.id } }, body: { client_org_ids: [...chosen] } })), onSuccess: () => { setAssign(null); void refresh(); } });
  const roles = ["owner", "admin", "staff", "viewer"].filter((r) => r !== "owner" || s.me?.role === "owner");
  return (
    <>
      <Card title="Add a team member" subtitle="Owner: everything. Admin: clients, channels and team. Staff: review and download. Viewer: read only.">
        <ErrorBanner error={invite.error} />
        <form className="ui-grid" onSubmit={(e) => { e.preventDefault(); invite.mutate(); }}>
          <Field label="Phone">{(id) => <Input id={id} value={inv.phone} onChange={(e) => setInv({ ...inv, phone: e.target.value })} />}</Field>
          <Field label="Name">{(id) => <Input id={id} value={inv.name} onChange={(e) => setInv({ ...inv, name: e.target.value })} />}</Field>
          <Field label="Role">{(id) => <Select id={id} value={inv.role} onChange={(e) => setInv({ ...inv, role: e.target.value })} options={roles.map((r) => ({ value: r, label: roleLabel(r) }))} />}</Field>
          <Field label="Client access">{(id) => <Select id={id} value={inv.client_access} onChange={(e) => setInv({ ...inv, client_access: e.target.value })}
            options={[{ value: "all", label: "All clients" }, { value: "assigned", label: "Assigned clients only" }]} />}</Field>
          <div><Button variant="primary" type="submit" busy={invite.isPending}>Add to team</Button></div>
        </form>
        {invited ? (
          <Notice tone="success">
            Added. They are not messaged automatically, so send them this:
            <p style={{ margin: "8px 0", whiteSpace: "pre-wrap" }}>{invited.text}</p>
            <span className="ui-row" style={{ gap: 8 }}>
              <CopyButton text={invited.text} label="Copy message" />
              <a className="ui-btn ui-btn-sm" target="_blank" rel="noreferrer"
                 href={`https://wa.me/${invited.phone.replace(/\D/g, "")}?text=${encodeURIComponent(invited.text)}`}><Icon name="message" size={16} />Open in WhatsApp</a>
            </span>
          </Notice>
        ) : null}
      </Card>
      <Card title="Members">
        <ErrorBanner error={patch.error ?? remove.error} />
        {q.isLoading ? <Spinner /> : <Table rows={q.data?.data ?? []} rowKey={(r) => r.id} columns={[
          { key: "n", header: "Member", render: (r) => <>{r.name ?? "—"}<div className="ui-hint">{maskPhone(r.phone)}</div></> },
          { key: "r", header: "Role", render: (r) => r.role === "owner" && s.me?.role !== "owner" ? roleLabel(r.role)
            : <Select aria-label="Role" value={r.role} onChange={(e) => patch.mutate({ id: r.id, body: { role: e.target.value } })} options={roles.map((x) => ({ value: x, label: roleLabel(x) }))} /> },
          { key: "a", header: "Clients", render: (r) => <Select aria-label="Client access" value={r.client_access}
            onChange={(e) => patch.mutate({ id: r.id, body: { client_access: e.target.value } })}
            options={[{ value: "all", label: "All" }, { value: "assigned", label: "Assigned" }]} /> },
          { key: "s", header: "Status", render: (r) => <Badge value={r.status} /> },
          { key: "x", header: "", render: (r) => (
            <div className="ui-row">
              {r.client_access === "assigned" ? <Button onClick={() => { setAssign(r); setChosen(new Set(r.client_org_ids ?? [])); }}>Assign clients ({r.client_org_ids?.length ?? 0})</Button> : null}
              <Button onClick={() => revoke.mutate(r.id)}>Log out devices</Button>
              <Button variant="danger" onClick={() => confirm("Remove this member?") && remove.mutate(r.id)}>Remove</Button>
            </div>) },
        ]} />}
      </Card>
      <Modal open={!!assign} title={`Clients for ${assign?.name ?? "member"}`} onClose={() => setAssign(null)}>
        <ErrorBanner error={saveAssign.error} />
        {(clients.data?.data ?? []).map((c) => (
          <label key={c.id} className="ui-row"><input type="checkbox" checked={chosen.has(c.id)} onChange={() => {
            const n = new Set(chosen); n.has(c.id) ? n.delete(c.id) : n.add(c.id); setChosen(n);
          }} />{c.display_name}</label>
        ))}
        <Button variant="primary" onClick={() => saveAssign.mutate()} busy={saveAssign.isPending}>{t("common.save")}</Button>
      </Modal>
    </>
  );
}

const SCOPES = ["items:read", "items:write", "connections:manage", "commands:gst", "commands:whatsapp", "commands:tally",
  "commands:storage", "commands:books", "commands:payments", "commands:commerce", "commands:marketplace", "commands:ca_tasks", "books:read",
  "documents:read", "mirror:manage", "events:read", "subscriptions:manage", "audit:read"];

function ApiKeys() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["api-keys"], queryFn: () => data<{ data: { id: string; name: string; key_prefix: string; scopes: string[]; created_at: string; revoked_at: string | null }[] }>(portal.GET("/portal/v1/api-keys")) });
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<Set<string>>(new Set(["items:read", "events:read"]));
  const [shown, setShown] = useState<string | null>(null);
  const create = useMutation({ mutationFn: () => data<{ key: string }>(portal.POST("/portal/v1/api-keys", { body: { name, scopes: [...scopes], allowed_ips: [] } })),
    onSuccess: (r) => { setShown(r.key); setName(""); void qc.invalidateQueries({ queryKey: ["api-keys"] }); } });
  const revoke = useMutation({ mutationFn: (id: string) => portal.DELETE("/portal/v1/api-keys/{key_id}", { params: { path: { key_id: id } } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["api-keys"] }) });
  return (
    <>
      {shown ? <Notice>Copy this key now — it is shown only once: <div className="ui-mono">{shown}</div><CopyButton text={shown} /></Notice> : null}
      <Card title="New key">
        <ErrorBanner error={create.error} />
        <form onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
          <Field label="Name">{(id) => <Input id={id} required value={name} onChange={(e) => setName(e.target.value)} />}</Field>
          <fieldset className="ui-grid"><legend>Scopes</legend>{SCOPES.map((sc) => (
            <label key={sc} className="ui-row"><input type="checkbox" checked={scopes.has(sc)} onChange={() => {
              const n = new Set(scopes); n.has(sc) ? n.delete(sc) : n.add(sc); setScopes(n); }} />{sc}</label>))}</fieldset>
          <Button variant="primary" type="submit" busy={create.isPending}>Create key</Button>
        </form>
      </Card>
      <Card title="Keys">
        <Table rows={q.data?.data ?? []} rowKey={(r) => r.id} columns={[
          { key: "n", header: "Name", render: (r) => r.name },
          { key: "p", header: "Prefix", render: (r) => <span className="ui-mono">ack_…_{r.key_prefix}</span> },
          { key: "s", header: "Scopes", render: (r) => r.scopes.join(", ") },
          { key: "c", header: "Created", render: (r) => fmtDate(r.created_at) },
          { key: "x", header: "", render: (r) => r.revoked_at ? <Badge value="revoked" /> : <Button variant="danger" onClick={() => revoke.mutate(r.id)}>Revoke</Button> },
        ]} />
      </Card>
    </>
  );
}

function Webhooks() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["subs"], queryFn: () => data<{ data: { id: string; url: string; event_types: string[]; status: string }[];
    recent_deliveries: { id: string; event_id: string; attempt: number; status: string; response_code: number | null; created_at: string }[] }>(portal.GET("/portal/v1/subscriptions")) });
  const [url, setUrl] = useState("https://");
  const [secret, setSecret] = useState<string | null>(null);
  const create = useMutation({ mutationFn: () => data<{ secret: string }>(portal.POST("/portal/v1/subscriptions", { body: { url, event_types: ["*"] } })),
    onSuccess: (r) => { setSecret(r.secret); void qc.invalidateQueries({ queryKey: ["subs"] }); } });
  return (
    <>
      {secret ? <Notice>Signing secret (shown once): <div className="ui-mono">{secret}</div><CopyButton text={secret} /></Notice> : null}
      <Card title="Add webhook">
        <ErrorBanner error={create.error} />
        <form className="ui-row" onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
          <Input type="url" aria-label="URL" value={url} onChange={(e) => setUrl(e.target.value)} />
          <Button variant="primary" type="submit" busy={create.isPending}>Add</Button>
        </form>
      </Card>
      <Card title="Subscriptions">
        <Table rows={q.data?.data ?? []} rowKey={(r) => r.id} columns={[
          { key: "u", header: "URL", render: (r) => <span className="ui-mono">{r.url}</span> },
          { key: "e", header: "Events", render: (r) => r.event_types.join(", ") },
          { key: "s", header: "Status", render: (r) => <Badge value={r.status} /> },
        ]} />
      </Card>
      <Card title="Recent deliveries">
        <Table rows={q.data?.recent_deliveries ?? []} rowKey={(r) => r.id} columns={[
          { key: "e", header: "Event", render: (r) => <span className="ui-mono">{r.event_id.slice(0, 8)}</span> },
          { key: "a", header: "Attempt", render: (r) => r.attempt },
          { key: "s", header: "Result", render: (r) => <Badge value={r.status} tone={r.status === "succeeded" ? "green" : "red"} /> },
          { key: "c", header: "HTTP", render: (r) => r.response_code ?? "—" },
          { key: "d", header: "When", render: (r) => fmtDate(r.created_at) },
        ]} />
      </Card>
    </>
  );
}

interface PolicyT { triage: { quarantine_threshold_not_financial: number; auto_resend_requests: boolean; llm_daily_budget_calls: number };
  retention: { accepted_years: number; quarantine_days: number }; mirror: { on_remove: string } }

function Policy() {
  const s = useSession();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["policy"], queryFn: () => data<PolicyT>(portal.GET("/portal/v1/settings/policy")) });
  const [draft, setDraft] = useState<PolicyT | null>(null);
  const save = useMutation({ mutationFn: (p: PolicyT) => data(portal.PATCH("/portal/v1/settings/policy", { body: p as never })),
    onSuccess: () => { setDraft(null); void qc.invalidateQueries({ queryKey: ["policy"] }); } });
  const p = draft ?? q.data;
  if (!p) return <Spinner />;
  const edit = s.can("tenant_settings");
  const up = (f: (x: PolicyT) => void) => { const n = structuredClone(p); f(n); setDraft(n); };
  return (
    <Card title="Document rules" subtitle="How unclear or non-business files are handled, and how long documents are kept.">
      <ErrorBanner error={save.error} />
      <div className="ui-setting-rows">
        <div className="ui-setting">
          <div><strong>Hold back non-business files</strong>
            <span>How sure AawazAI must be that a file is not financial before holding it back. Higher holds back fewer files (0.85 to 0.99).</span></div>
          <Input aria-label="Hold back threshold" type="number" step="0.01" min={0.85} max={0.99} disabled={!edit} value={p.triage.quarantine_threshold_not_financial}
                 onChange={(e) => up((n) => { n.triage.quarantine_threshold_not_financial = Number(e.target.value); })} />
        </div>
        <div className="ui-setting">
          <div><strong>Keep filed documents</strong><span>Years documents stay in AawazAI after they are filed.</span></div>
          <div className="ui-input-unit"><Input aria-label="Years" type="number" min={1} disabled={!edit} value={p.retention.accepted_years}
            onChange={(e) => up((n) => { n.retention.accepted_years = Number(e.target.value); })} /><span>years</span></div>
        </div>
        <div className="ui-setting">
          <div><strong>Keep held-back files</strong><span>Days before held-back files are deleted (7 to 90).</span></div>
          <div className="ui-input-unit"><Input aria-label="Days" type="number" min={7} max={90} disabled={!edit} value={p.retention.quarantine_days}
            onChange={(e) => up((n) => { n.retention.quarantine_days = Number(e.target.value); })} /><span>days</span></div>
        </div>
        <div className="ui-setting">
          <div><strong>Ask for a clearer photo</strong><span>Reply on WhatsApp when a photo is too blurry to read; at most 3 per chat per hour.</span></div>
          <input type="checkbox" role="switch" className="ui-switch" aria-label="Ask for a clearer photo" disabled={!edit} checked={p.triage.auto_resend_requests}
                 onChange={(e) => up((n) => { n.triage.auto_resend_requests = e.target.checked; })} />
        </div>
      </div>
      {edit && draft ? (
        <div className="ui-save-bar">
          <span>You have unsaved changes.</span>
          <Button variant="ghost" onClick={() => setDraft(null)}>Discard</Button>
          <Button variant="primary" onClick={() => save.mutate(draft)} busy={save.isPending}>{t("common.save")}</Button>
        </div>
      ) : null}
    </Card>
  );
}

function Audit() {
  const q = useQuery({ queryKey: ["audit"], queryFn: () => data<{ data: { id: number; actor_type: string; actor_id: string | null; action: string;
    target_type: string; target_id: string | null; grant_id: string | null; created_at: string }[] }>(portal.GET("/portal/v1/audit", { params: { query: { limit: 200 } } })) });
  const rows = [...(q.data?.data ?? [])].reverse();
  return (
    <Card title="Audit log">
      {q.isLoading ? <Spinner /> : <Table rows={rows} rowKey={(r) => String(r.id)} columns={[
        { key: "d", header: "When", render: (r) => fmtDate(r.created_at) },
        { key: "a", header: "Action", render: (r) => r.action },
        { key: "w", header: "Who", render: (r) => <>{r.actor_type}{r.grant_id ? <Badge value="support grant" tone="amber" /> : null}</> },
        { key: "t", header: "Target", render: (r) => `${r.target_type} ${r.target_id?.slice(0, 8) ?? ""}` },
      ]} />}
    </Card>
  );
}

function Support() {
  const s = useSession();
  const qc = useQueryClient();
  const grants = useQuery({ queryKey: ["grants"], queryFn: () => data<{ data: { id: string; scope: string; reason: string; ticket_ref: string | null;
    granted_via: string; state: string; requested_minutes: number; expires_at: string | null; created_at: string }[] }>(portal.GET("/portal/v1/support-grants")) });
  const notices = useQuery({ queryKey: ["notices"], queryFn: () => data<{ data: { at: string; text: string }[] }>(portal.GET("/portal/v1/notices")) });
  const decide = useMutation({ mutationFn: ({ id, d }: { id: string; d: "approve" | "deny" }) => data(portal.POST(
    "/portal/v1/support-grants/{grant_id}:{decision}", { params: { path: { grant_id: id, decision: d } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["grants"] }) });
  return (
    <>
      {(notices.data?.data ?? []).map((n) => <Notice key={n.at}>{fmtDate(n.at)} — {n.text}</Notice>)}
      <Card title="AawazAI support access">
        <p className="ui-hint">AawazAI staff can see names, counts and statuses only. Viewing your documents needs your approval and expires automatically.</p>
        <ErrorBanner error={decide.error} />
        {(grants.data?.data.length ?? 0) === 0 ? <Empty>No requests.</Empty> : <Table rows={grants.data!.data} rowKey={(r) => r.id} columns={[
          { key: "r", header: "Reason", render: (r) => <>{r.reason}{r.ticket_ref ? <div className="ui-hint">Ticket {r.ticket_ref}</div> : null}</> },
          { key: "s", header: "Access", render: (r) => `${r.scope} · ${r.granted_via.replace("_", " ")}` },
          { key: "d", header: "Duration", render: (r) => `${Math.round(r.requested_minutes / 60 * 10) / 10} h` },
          { key: "t", header: "State", render: (r) => <><Badge value={r.state} />{r.expires_at && r.state === "active" ? <div className="ui-hint">until {fmtDate(r.expires_at)}</div> : null}</> },
          { key: "x", header: "", render: (r) => r.state === "pending" && s.me?.role === "owner" ? (
            <div className="ui-row"><Button variant="primary" onClick={() => decide.mutate({ id: r.id, d: "approve" })}>Approve</Button>
              <Button onClick={() => decide.mutate({ id: r.id, d: "deny" })}>Deny</Button></div>) : null },
        ]} />}
      </Card>
    </>
  );
}

function DataTab() {
  const clients = useClients();
  const [client, setClient] = useState("");
  const [exp, setExp] = useState<{ id: string } | null>(null);
  const status = useQuery({ enabled: !!exp, queryKey: ["export", exp?.id], refetchInterval: (q) => (q.state.data as { state?: string } | undefined)?.state === "done" ? false : 2000,
    queryFn: () => data<{ state: string; items?: number; download_url?: string }>(portal.GET("/portal/v1/exports/{export_id}", { params: { path: { export_id: exp!.id } } })) });
  const start = useMutation({ mutationFn: () => data<{ id: string }>(portal.POST("/portal/v1/exports", { body: { client_org_id: client || null } })), onSuccess: setExp });
  const [eraseId, setEraseId] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const erase = useMutation({ mutationFn: () => data<{ erasure: { state: string } }>(portal.POST("/portal/v1/clients/{client_id}:erase", { params: { path: { client_id: eraseId } } })) });
  const target = clients.data?.data.find((c) => c.id === eraseId);
  return (
    <>
      <Card title="Export">
        <ErrorBanner error={start.error} />
        <div className="ui-row">
          <Select aria-label="Client" value={client} onChange={(e) => setClient(e.target.value)}
                  options={[{ value: "", label: "All clients" }, ...(clients.data?.data ?? []).map((c) => ({ value: c.id, label: c.display_name }))]} />
          <Button variant="primary" onClick={() => start.mutate()} busy={start.isPending}>Export accepted documents</Button>
        </div>
        {status.data ? <p>Export {status.data.state}{status.data.items != null ? ` · ${status.data.items} files` : ""}{" "}
          {status.data.download_url ? <a href={status.data.download_url}>Download zip</a> : <Spinner small />}</p> : null}
      </Card>
      <Card title="Erase a client (right to erasure)">
        <p className="ui-hint">Deletes this client's documents from AawazAI and removes AawazAI's mirror copies. Entries already posted into
          Tally are your books and are not touched; they are listed in the erasure report. Refused while under legal hold.</p>
        <ErrorBanner error={erase.error} />
        <div className="ui-grid">
          <Select aria-label="Client" value={eraseId} onChange={(e) => setEraseId(e.target.value)}
                  options={[{ value: "", label: "Choose client" }, ...(clients.data?.data ?? []).map((c) => ({ value: c.id, label: c.display_name }))]} />
          <Input aria-label="Type the client name to confirm" placeholder="Type the client name to confirm" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} />
          <Button variant="danger" disabled={!target || confirmText !== target.display_name} busy={erase.isPending} onClick={() => erase.mutate()}>Erase permanently</Button>
        </div>
        {erase.data ? <Notice>Erasure {erase.data.erasure.state}. It runs in the background: the client's documents leave Documents as it completes, and the result is recorded in Activity.</Notice> : null}
      </Card>
    </>
  );
}

function Devices() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["devices"], queryFn: () => data<{ data: { id: string; device: { ua?: string }; ip: string | null; last_seen_at: string; current: boolean }[] }>(portal.GET("/portal/v1/auth/sessions")) });
  const all = useMutation({ mutationFn: () => data(portal.POST("/portal/v1/auth/sessions:revoke-all")), onSuccess: () => window.location.assign("/login") });
  return (
    <Card title="Signed-in devices" actions={<Button variant="danger" onClick={() => all.mutate()}>Log out everywhere</Button>}>
      <Table rows={q.data?.data ?? []} rowKey={(r) => r.id} columns={[
        { key: "d", header: "Device", render: (r) => <>{r.device.ua?.slice(0, 60) ?? "—"}{r.current ? <Badge value="this device" tone="blue" /> : null}</> },
        { key: "i", header: "IP", render: (r) => r.ip ?? "—" },
        { key: "l", header: "Last seen", render: (r) => fmtDate(r.last_seen_at) },
      ]} />
      <button hidden onClick={() => void qc.invalidateQueries()} />
    </Card>
  );
}
